import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import usePageMeta from '@/hooks/usePageMeta';
import {
  getArticleById,
  getCategoryBySlug,
  getRelatedArticles,
  hotArticles,
} from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

const formatViews = (views: number) => `${(views / 10000).toFixed(1)} 万`;

export default function ArticleDetail() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const category = article ? getCategoryBySlug(article.category) : undefined;
  const related = article ? getRelatedArticles(article, 3) : [];

  const toc = useMemo(
    () =>
      (article?.blocks ?? [])
        .map((block, index) => ({ ...block, index }))
        .filter((block) => Boolean(block.heading)),
    [article],
  );

  usePageMeta({
    title: article ? `${article.title} - 看料99` : '内容不存在 - 看料99',
    description: article
      ? article.summary.slice(0, 150)
      : '该内容暂不可用，请返回看料99 首页浏览其他爆料。',
    keywords: article
      ? [article.title, ...article.tags, '看料99', '看料99首发爆料'].join(',')
      : '看料99',
    path: `/article/${id}`,
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.cover,
          datePublished: article.publishedAt,
          articleSection: category?.name,
          keywords: article.tags.join(','),
          author: { '@type': 'Organization', name: article.author },
          publisher: { '@type': 'Organization', name: '看料99' },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://lfthg.cn/article/${article.id}`,
          },
        }
      : undefined,
  });

  if (!article || !category) {
    return <NotFound />;
  }

  return (
    <SiteLayout>
      <div className="animate-fade-up">
        <div className="mx-auto w-full max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '全部分类', to: '/category' },
              { label: category.name, to: `/category/${category.slug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_280px]">
            <div className="min-w-0">
              <header>
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900 transition-colors hover:bg-accent-200"
                >
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {category.name}
                </Link>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {article.summary}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-3 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" aria-hidden="true"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {formatViews(article.views)} 阅读
                  </span>
                </div>
              </header>

              <div className="mt-6 h-[240px] w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 看料99`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8 space-y-6">
                {article.blocks.map((block, index) => (
                  <section key={index} id={`section-${index}`} className="scroll-mt-24">
                    {block.heading && (
                      <h2 className="mb-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                        {block.heading}
                      </h2>
                    )}
                    <p className="text-sm leading-loose text-foreground-700 md:text-base">
                      {block.text}
                    </p>
                  </section>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">标签</span>
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>

            <aside className="space-y-8">
              {toc.length > 0 && (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">本页目录</h2>
                  <ul className="mt-3 space-y-2 border-l border-background-200 pl-4">
                    {toc.map((item) => (
                      <li key={item.index}>
                        <a
                          href={`#section-${item.index}`}
                          className="text-sm text-foreground-600 transition-colors hover:text-primary-600"
                        >
                          {item.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-1.5 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                  热门爆料
                </h2>
                <ol className="mt-3 space-y-1">
                  {hotArticles.slice(0, 5).map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/article/${item.id}`}
                        className="group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-50"
                      >
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                            index < 3
                              ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                              : 'bg-background-300 text-foreground-700'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          </div>

          <section aria-label="相关推荐" className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        </article>
      </div>
    </SiteLayout>
  );
}